export const WEB_VITAL_NAMES = ["LCP", "INP", "CLS", "FCP", "TTFB"];
export const WEB_VITAL_ISSUE_THRESHOLDS = {
    LCP: { value: 2500, priority: "p0" },
    INP: { value: 200, priority: "p0" },
    CLS: { value: 0.1, priority: "p0" },
    FCP: { value: 1800, priority: "p1" },
    TTFB: { value: 800, priority: "p1" },
};
export const WEB_VITAL_EXCLUDED_ROUTE_PREFIXES = [
    "/admin",
    "/analytics",
    "/embedded",
];
export const DEFAULT_BACKEND_TELEMETRY_CONFIG = {
    projectId: "stanza-react-app",
    requestMetricsView: "stanza-react-app.backend_telemetry.backend_request_metrics_flush_v1",
    slowRequestsView: "stanza-react-app.backend_telemetry.backend_slow_requests_v1",
};
const ROUTE_REPORT_FILTER_SQL = "route_template != 'UNMATCHED' AND method != 'OPTIONS'";
export function formatBigQueryTableSuffix(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}${month}${day}`;
}
export function isWebVitalsExcludedRoute(route) {
    if (!route)
        return false;
    return WEB_VITAL_EXCLUDED_ROUTE_PREFIXES.some((prefix) => route === prefix || route.startsWith(`${prefix}/`));
}
export async function queryWebVitalsSummary(options) {
    const { sql, params, types } = webVitalsQueryBase(options, options.segmentType ?? null);
    const rows = await options.bq.runQuery(`${sql}
    SELECT
      metric_name,
      segment_type,
      segment_value,
      COUNT(*) AS sample_count,
      COUNT(DISTINCT user_pseudo_id) AS user_count,
      ROUND(APPROX_QUANTILES(metric_value, 100)[OFFSET(75)], 3) AS p75,
      ROUND(APPROX_QUANTILES(metric_value, 100)[OFFSET(90)], 3) AS p90,
      ROUND(AVG(metric_value), 3) AS avg_value,
      COUNTIF(metric_rating = "good") AS good_count,
      COUNTIF(metric_rating = "needs-improvement") AS needs_improvement_count,
      COUNTIF(metric_rating = "poor") AS poor_count
    FROM segmented
    WHERE (@metric IS NULL OR metric_name = @metric)
      AND (@segmentType IS NULL OR segment_type = @segmentType)
      AND (@segmentValue IS NULL OR segment_value = @segmentValue)
    GROUP BY metric_name, segment_type, segment_value
    ORDER BY metric_name, segment_type, sample_count DESC`, {
        params,
        types,
    });
    return rows;
}
export async function queryWebVitalsTrend(options) {
    const segmentType = options.segmentType ?? "overall";
    const { sql, params, types } = webVitalsQueryBase(options, segmentType);
    const rows = await options.bq.runQuery(`${sql}
    SELECT
      event_date,
      metric_name,
      COUNT(*) AS sample_count,
      COUNT(DISTINCT user_pseudo_id) AS user_count,
      ROUND(APPROX_QUANTILES(metric_value, 100)[OFFSET(75)], 3) AS p75,
      ROUND(APPROX_QUANTILES(metric_value, 100)[OFFSET(90)], 3) AS p90,
      ROUND(AVG(metric_value), 3) AS avg_value,
      COUNTIF(metric_rating = "good") AS good_count,
      COUNTIF(metric_rating = "needs-improvement") AS needs_improvement_count,
      COUNTIF(metric_rating = "poor") AS poor_count
    FROM segmented
    WHERE segment_type = @segmentType
      AND (@metric IS NULL OR metric_name = @metric)
      AND (@segmentValue IS NULL OR segment_value = @segmentValue)
    GROUP BY event_date, metric_name
    ORDER BY event_date ASC, metric_name ASC`, {
        params,
        types,
    });
    return rows;
}
export async function queryBackendRouteSummary(options) {
    const { params, types, requestMetricsView, slowRequestsView } = backendQueryOptions(options);
    return options.bq.runQuery(`
    WITH route_metrics AS (
      SELECT
        route_template,
        method,
        SUM(request_count) AS requests,
        SUM(error_count) AS errors,
        SAFE_DIVIDE(SUM(error_count), SUM(request_count)) AS error_rate,
        SAFE_DIVIDE(SUM(duration_ms_total), SUM(request_count)) AS mean_duration_ms,
        MAX(duration_ms_max) AS max_duration_ms
      FROM \`${requestMetricsView}\`
      WHERE timestamp >= @startTime
        AND timestamp < @endTime
        AND ${ROUTE_REPORT_FILTER_SQL}
        AND (@routeTemplate IS NULL OR route_template = @routeTemplate)
      GROUP BY route_template, method
    ),
    slow_metrics AS (
      SELECT
        route_template,
        method,
        COUNT(*) AS slow_request_count,
        AVG(duration_ms) AS avg_slow_duration_ms,
        MAX(duration_ms) AS max_slow_duration_ms
      FROM \`${slowRequestsView}\`
      WHERE timestamp >= @startTime
        AND timestamp < @endTime
        AND ${ROUTE_REPORT_FILTER_SQL}
        AND (@routeTemplate IS NULL OR route_template = @routeTemplate)
      GROUP BY route_template, method
    )
    SELECT
      r.route_template,
      r.method,
      r.requests,
      r.errors,
      r.error_rate,
      r.mean_duration_ms,
      r.max_duration_ms,
      COALESCE(s.slow_request_count, 0) AS slow_request_count,
      COALESCE(s.avg_slow_duration_ms, 0) AS avg_slow_duration_ms,
      COALESCE(s.max_slow_duration_ms, 0) AS max_slow_duration_ms
    FROM route_metrics r
    LEFT JOIN slow_metrics s USING (route_template, method)
    WHERE r.requests >= @minRequests
    ORDER BY r.requests DESC`, { params, types });
}
export async function queryBackendRouteTrend(options) {
    const { params, types, requestMetricsView, slowRequestsView } = backendQueryOptions(options);
    return options.bq.runQuery(`
    WITH route_metrics AS (
      SELECT
        FORMAT_TIMESTAMP("%F", timestamp) AS event_date,
        route_template,
        method,
        SUM(request_count) AS requests,
        SUM(error_count) AS errors,
        SAFE_DIVIDE(SUM(error_count), SUM(request_count)) AS error_rate,
        SAFE_DIVIDE(SUM(duration_ms_total), SUM(request_count)) AS mean_duration_ms,
        MAX(duration_ms_max) AS max_duration_ms,
        ARRAY_AGG(DISTINCT IF(app_deploy_tag = "", NULL, app_deploy_tag) IGNORE NULLS) AS deploy_tags
      FROM \`${requestMetricsView}\`
      WHERE timestamp >= @startTime
        AND timestamp < @endTime
        AND ${ROUTE_REPORT_FILTER_SQL}
        AND (@routeTemplate IS NULL OR route_template = @routeTemplate)
      GROUP BY event_date, route_template, method
    ),
    slow_metrics AS (
      SELECT
        FORMAT_TIMESTAMP("%F", timestamp) AS event_date,
        route_template,
        method,
        COUNT(*) AS slow_request_count,
        AVG(duration_ms) AS avg_slow_duration_ms,
        MAX(duration_ms) AS max_slow_duration_ms
      FROM \`${slowRequestsView}\`
      WHERE timestamp >= @startTime
        AND timestamp < @endTime
        AND ${ROUTE_REPORT_FILTER_SQL}
        AND (@routeTemplate IS NULL OR route_template = @routeTemplate)
      GROUP BY event_date, route_template, method
    )
    SELECT
      r.event_date,
      r.route_template,
      r.method,
      r.requests,
      r.errors,
      r.error_rate,
      r.mean_duration_ms,
      r.max_duration_ms,
      COALESCE(s.slow_request_count, 0) AS slow_request_count,
      COALESCE(s.avg_slow_duration_ms, 0) AS avg_slow_duration_ms,
      COALESCE(s.max_slow_duration_ms, 0) AS max_slow_duration_ms,
      r.deploy_tags
    FROM route_metrics r
    LEFT JOIN slow_metrics s USING (event_date, route_template, method)
    WHERE r.requests >= @minRequests
    ORDER BY r.event_date ASC, r.route_template ASC, r.method ASC`, { params, types });
}
export async function queryBackendDeploys(options) {
    const { params, types, requestMetricsView } = backendQueryOptions(options);
    return options.bq.runQuery(`
    SELECT
      app_deploy_tag,
      CAST(MIN(timestamp) AS STRING) AS first_seen_at
    FROM \`${requestMetricsView}\`
    WHERE timestamp >= @startTime
      AND timestamp < @endTime
      AND ${ROUTE_REPORT_FILTER_SQL}
      AND (@routeTemplate IS NULL OR route_template = @routeTemplate)
      AND app_deploy_tag IS NOT NULL
      AND app_deploy_tag != ""
    GROUP BY app_deploy_tag
    ORDER BY first_seen_at ASC`, { params, types });
}
function webVitalsQueryBase(options, segmentType) {
    const startSuffix = formatBigQueryTableSuffix(options.start);
    const endSuffix = formatBigQueryTableSuffix(options.end);
    const intradayTodaySuffix = endSuffix;
    const intradayYesterdaySuffix = formatBigQueryTableSuffix(new Date(options.end.getTime() - 86_400_000));
    const fullTablePrefix = `${options.projectId}.${options.dataset}`;
    return {
        sql: `
      WITH all_events AS (
        SELECT event_name, event_timestamp, user_pseudo_id, event_params,
          device.category AS device_category, geo.country AS country
        FROM \`${fullTablePrefix}.events_*\`
        WHERE _TABLE_SUFFIX BETWEEN @startSuffix AND @endSuffix
          AND event_name IN ("LCP", "INP", "CLS", "FCP", "TTFB")
        UNION ALL
        SELECT event_name, event_timestamp, user_pseudo_id, event_params,
          device.category AS device_category, geo.country AS country
        FROM \`${fullTablePrefix}.events_intraday_*\`
        WHERE _TABLE_SUFFIX IN (@intradayTodaySuffix, @intradayYesterdaySuffix)
          AND event_name IN ("LCP", "INP", "CLS", "FCP", "TTFB")
      ),
      normalized AS (
        SELECT
          event_name AS metric_name,
          event_timestamp,
          user_pseudo_id,
          COALESCE(device_category, "unknown") AS device_category,
          CASE WHEN country = "United States" THEN "US" ELSE "International" END AS region,
          NULLIF((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "web_vitals_route"), "") AS web_vitals_route,
          COALESCE(
            (SELECT value.double_value FROM UNNEST(event_params) WHERE key = "metric_value"),
            SAFE_CAST((SELECT value.int_value FROM UNNEST(event_params) WHERE key = "metric_value") AS FLOAT64),
            SAFE_CAST((SELECT value.float_value FROM UNNEST(event_params) WHERE key = "metric_value") AS FLOAT64),
            SAFE_CAST((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "metric_value") AS FLOAT64)
          ) AS metric_value,
          COALESCE((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "metric_rating"), "(not set)") AS metric_rating,
          COALESCE((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "visibility_state_at_load"), "(not set)") AS visibility_state_at_load,
          COALESCE((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "metric_navigation_type"), "(not set)") AS metric_navigation_type,
          COALESCE((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "event_category"), "(not set)") AS event_category,
          COALESCE((SELECT value.string_value FROM UNNEST(event_params) WHERE key = "metric_name"), "(not set)") AS metric_param_name
        FROM all_events
        WHERE TIMESTAMP_MICROS(event_timestamp) >= @startTime
          AND TIMESTAMP_MICROS(event_timestamp) < @endTime
      ),
      web_vitals AS (
        SELECT * FROM normalized
        WHERE event_category = "Web Vitals"
          AND metric_param_name = metric_name
          AND metric_value IS NOT NULL
          AND (
            web_vitals_route IS NULL
            OR NOT EXISTS (
              SELECT 1 FROM UNNEST(@excludedRoutePrefixes) AS prefix
              WHERE web_vitals_route = prefix
                OR STARTS_WITH(web_vitals_route, CONCAT(prefix, "/"))
            )
          )
      ),
      segmented AS (
        SELECT metric_name, FORMAT_TIMESTAMP("%F", TIMESTAMP_MICROS(event_timestamp)) AS event_date,
          "overall" AS segment_type, "All" AS segment_value, user_pseudo_id, metric_value, metric_rating
        FROM web_vitals
        UNION ALL
        SELECT metric_name, FORMAT_TIMESTAMP("%F", TIMESTAMP_MICROS(event_timestamp)) AS event_date,
          "device" AS segment_type, device_category AS segment_value, user_pseudo_id, metric_value, metric_rating
        FROM web_vitals
        UNION ALL
        SELECT metric_name, FORMAT_TIMESTAMP("%F", TIMESTAMP_MICROS(event_timestamp)) AS event_date,
          "region" AS segment_type, region AS segment_value, user_pseudo_id, metric_value, metric_rating
        FROM web_vitals
        UNION ALL
        SELECT metric_name, FORMAT_TIMESTAMP("%F", TIMESTAMP_MICROS(event_timestamp)) AS event_date,
          "route" AS segment_type, web_vitals_route AS segment_value, user_pseudo_id, metric_value, metric_rating
        FROM web_vitals
        WHERE web_vitals_route IS NOT NULL
        UNION ALL
        SELECT metric_name, FORMAT_TIMESTAMP("%F", TIMESTAMP_MICROS(event_timestamp)) AS event_date,
          "visibility" AS segment_type, visibility_state_at_load AS segment_value, user_pseudo_id, metric_value, metric_rating
        FROM web_vitals
        UNION ALL
        SELECT metric_name, FORMAT_TIMESTAMP("%F", TIMESTAMP_MICROS(event_timestamp)) AS event_date,
          "navigation" AS segment_type, metric_navigation_type AS segment_value, user_pseudo_id, metric_value, metric_rating
        FROM web_vitals
      )`,
        params: {
            startSuffix,
            endSuffix,
            intradayTodaySuffix,
            intradayYesterdaySuffix,
            startTime: options.start,
            endTime: options.end,
            metric: options.metric ?? null,
            segmentType,
            segmentValue: options.segmentValue ?? null,
            excludedRoutePrefixes: [...WEB_VITAL_EXCLUDED_ROUTE_PREFIXES],
        },
        types: {
            startSuffix: "STRING",
            endSuffix: "STRING",
            intradayTodaySuffix: "STRING",
            intradayYesterdaySuffix: "STRING",
            startTime: "TIMESTAMP",
            endTime: "TIMESTAMP",
            metric: "STRING",
            segmentType: "STRING",
            segmentValue: "STRING",
            excludedRoutePrefixes: ["STRING"],
        },
    };
}
function backendQueryOptions(options) {
    const config = options.config ?? DEFAULT_BACKEND_TELEMETRY_CONFIG;
    return {
        requestMetricsView: config.requestMetricsView,
        slowRequestsView: config.slowRequestsView,
        params: {
            startTime: options.start,
            endTime: options.end,
            routeTemplate: options.routeTemplate ?? null,
            minRequests: options.minRequests ?? 0,
        },
        types: {
            startTime: "TIMESTAMP",
            endTime: "TIMESTAMP",
            routeTemplate: "STRING",
            minRequests: "INT64",
        },
    };
}
//# sourceMappingURL=PerformanceAnalyticsService.js.map